import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Save,
  FileDown,
  HardHat,
  Copy,
  FileText,
  Calendar,
  Phone,
  MapPin,
  User,
  Globe,
  Building2,
  Pen,
  RotateCcw,
  Users,
  X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Language options
const LANGUAGES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "no", label: "Norsk", flag: "🇳🇴" },
  { code: "ro", label: "Română", flag: "🇷🇴" },
];

// Translations
const TRANSLATIONS = {
  en: {
    // Form labels
    generalInfo: "General Information",
    jobDescription: "Job Description",
    workLocation: "Work Location",
    workDate: "Work Date",
    identifiedHazards: "Identified Hazards",
    hazards: "hazards",
    ppe: "Personal Protective Equipment (PPE)",
    emergencyInfo: "Emergency Information",
    emergencyContact: "Emergency Contact",
    nearestHospital: "Nearest Hospital",
    firstAidLocation: "First Aid Kit Location",
    additionalMeasures: "Additional Measures",
    preparedBy: "Prepared By",
    preparedDate: "Prepared Date",
    probability: "Probability",
    consequence: "Consequence",
    responsiblePerson: "Responsible Person",
    preventiveMeasures: "Preventive Measures",
    low: "Low",
    medium: "Medium", 
    high: "High",
    minor: "Minor",
    moderate: "Moderate",
    severe: "Severe",
    lowRisk: "Low Risk",
    mediumRisk: "Medium Risk",
    highRisk: "High Risk",
    save: "Save SJA",
    update: "Update SJA",
    cancel: "Cancel",
    downloadPdf: "Download PDF",
    importPrevious: "Import from previous SJA",
    language: "Language",
    // Participants
    participants: "Participants",
    addParticipant: "Add Participant",
    company: "Company",
    fullName: "Full Name",
    signature: "Signature",
    addSignature: "Add Signature",
    noParticipants: "No participants added yet",
    // Hazards
    hazardLabels: {
      fall_height: "Fall from height",
      falling_objects: "Falling objects",
      weather: "Weather conditions",
      traffic: "Traffic",
      electrical: "Electrical hazard",
      manual_handling: "Manual handling",
      confined_space: "Confined spaces",
      noise: "Noise",
      chemicals: "Chemicals",
      other: "Other"
    },
    // PPE
    ppeLabels: {
      helmet: "Helmet",
      harness: "Fall protection/Harness",
      gloves: "Gloves",
      safety_shoes: "Safety shoes",
      hi_vis: "High-visibility clothing",
      glasses: "Safety glasses",
      hearing: "Hearing protection",
      mask: "Mask/Respirator"
    }
  },
  no: {
    // Form labels
    generalInfo: "Generell Informasjon",
    jobDescription: "Arbeidsbeskrivelse",
    workLocation: "Arbeidssted",
    workDate: "Arbeidsdato",
    identifiedHazards: "Identifiserte Farer",
    hazards: "farer",
    ppe: "Personlig Verneutstyr (PPE)",
    emergencyInfo: "Nødinformasjon",
    emergencyContact: "Nødkontakt",
    nearestHospital: "Nærmeste Sykehus",
    firstAidLocation: "Førstehjelpsutstyr Plassering",
    additionalMeasures: "Tilleggstiltak",
    preparedBy: "Utarbeidet av",
    preparedDate: "Utarbeidet Dato",
    probability: "Sannsynlighet",
    consequence: "Konsekvens",
    responsiblePerson: "Ansvarlig Person",
    preventiveMeasures: "Forebyggende Tiltak",
    low: "Lav",
    medium: "Middels",
    high: "Høy",
    minor: "Liten",
    moderate: "Moderat",
    severe: "Alvorlig",
    lowRisk: "Lav Risiko",
    mediumRisk: "Middels Risiko",
    highRisk: "Høy Risiko",
    save: "Lagre SJA",
    update: "Oppdater SJA",
    cancel: "Avbryt",
    downloadPdf: "Last ned PDF",
    importPrevious: "Importer fra tidligere SJA",
    language: "Språk",
    // Participants
    participants: "Deltakere",
    addParticipant: "Legg til Deltaker",
    company: "Firma",
    fullName: "Fullt Navn",
    signature: "Signatur",
    addSignature: "Legg til Signatur",
    noParticipants: "Ingen deltakere lagt til ennå",
    // Hazards
    hazardLabels: {
      fall_height: "Fall fra høyde",
      falling_objects: "Fallende gjenstander",
      weather: "Værforhold",
      traffic: "Trafikk",
      electrical: "Elektrisk fare",
      manual_handling: "Manuell håndtering",
      confined_space: "Trange rom",
      noise: "Støy",
      chemicals: "Kjemikalier",
      other: "Annet"
    },
    // PPE
    ppeLabels: {
      helmet: "Hjelm",
      harness: "Fallsikring/Sele",
      gloves: "Hansker",
      safety_shoes: "Vernesko",
      hi_vis: "Synlighetsklær",
      glasses: "Vernebriller",
      hearing: "Hørselvern",
      mask: "Maske/Åndedrettsvern"
    }
  },
  ro: {
    // Form labels
    generalInfo: "Informații Generale",
    jobDescription: "Descrierea Lucrării",
    workLocation: "Locația Lucrării",
    workDate: "Data Lucrării",
    identifiedHazards: "Hazarde Identificate",
    hazards: "hazarde",
    ppe: "Echipament de Protecție (PPE)",
    emergencyInfo: "Informații de Urgență",
    emergencyContact: "Contact Urgență",
    nearestHospital: "Cel Mai Apropiat Spital",
    firstAidLocation: "Locația Trusă Prim Ajutor",
    additionalMeasures: "Măsuri Suplimentare",
    preparedBy: "Întocmit de",
    preparedDate: "Data Întocmirii",
    probability: "Probabilitate",
    consequence: "Consecință",
    responsiblePerson: "Persoana Responsabilă",
    preventiveMeasures: "Măsuri Preventive",
    low: "Scăzută",
    medium: "Medie",
    high: "Ridicată",
    minor: "Minoră",
    moderate: "Moderată",
    severe: "Severă",
    lowRisk: "Risc Scăzut",
    mediumRisk: "Risc Mediu",
    highRisk: "Risc Ridicat",
    save: "Salvează SJA",
    update: "Actualizează SJA",
    cancel: "Anulează",
    downloadPdf: "Descarcă PDF",
    importPrevious: "Import din SJA anterior",
    language: "Limbă",
    // Participants
    participants: "Participanți",
    addParticipant: "Adaugă Participant",
    company: "Companie",
    fullName: "Nume Prenume",
    signature: "Semnătură",
    addSignature: "Adaugă Semnătură",
    noParticipants: "Nu sunt participanți adăugați",
    // Hazards
    hazardLabels: {
      fall_height: "Cădere de la înălțime",
      falling_objects: "Obiecte în cădere",
      weather: "Condiții meteo",
      traffic: "Trafic",
      electrical: "Pericol electric",
      manual_handling: "Manipulare manuală",
      confined_space: "Spații înguste",
      noise: "Zgomot",
      chemicals: "Substanțe chimice",
      other: "Altele"
    },
    // PPE
    ppeLabels: {
      helmet: "Cască",
      harness: "Ham de siguranță",
      gloves: "Mănuși",
      safety_shoes: "Încălțăminte de protecție",
      hi_vis: "Vestă reflectorizantă",
      glasses: "Ochelari de protecție",
      hearing: "Protecție auditivă",
      mask: "Mască/Respirator"
    }
  }
};

// Standard SJA Hazards (with IDs for translation lookup)
const COMMON_HAZARDS = [
  { id: "fall_height", description: "Working at height, scaffolding, roof" },
  { id: "falling_objects", description: "Tools, materials that may fall" },
  { id: "weather", description: "Wind, rain, ice, cold" },
  { id: "traffic", description: "Vehicles, pedestrians in the area" },
  { id: "electrical", description: "Live wires, equipment" },
  { id: "manual_handling", description: "Heavy lifting, strain" },
  { id: "confined_space", description: "Limited space, poor ventilation" },
  { id: "noise", description: "High noise levels from machinery" },
  { id: "chemicals", description: "Hazardous substances, dust" },
  { id: "other", description: "Other identified hazards" },
];

// PPE Options (with IDs for translation lookup)
const PPE_OPTIONS = [
  { id: "helmet", icon: "🪖" },
  { id: "harness", icon: "🦺" },
  { id: "gloves", icon: "🧤" },
  { id: "safety_shoes", icon: "👢" },
  { id: "hi_vis", icon: "🦺" },
  { id: "glasses", icon: "🥽" },
  { id: "hearing", icon: "🎧" },
  { id: "mask", icon: "😷" },
];

// Risk level calculator
const calculateRiskLevel = (probability, consequence) => {
  const matrix = {
    low: { low: "green", medium: "yellow", high: "yellow" },
    medium: { low: "yellow", medium: "yellow", high: "red" },
    high: { low: "yellow", medium: "red", high: "red" },
  };
  return matrix[probability]?.[consequence] || "yellow";
};

// Risk badge component
const getRiskBadge = (level, t) => {
  const styles = {
    green: "bg-green-500/20 text-green-400 border-green-500/50",
    yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
    red: "bg-red-500/20 text-red-400 border-red-500/50",
  };
  const labels = {
    green: t?.lowRisk || "Low Risk",
    yellow: t?.mediumRisk || "Medium Risk",
    red: t?.highRisk || "High Risk",
  };
  return (
    <Badge variant="outline" className={styles[level] || styles.yellow}>
      {labels[level] || t?.mediumRisk || "Medium Risk"}
    </Badge>
  );
};

// Signature Pad Component for Participants
const ParticipantSignaturePad = ({ isOpen, onClose, onSave }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (isOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      setIsEmpty(true);
    }
  }, [isOpen]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setIsEmpty(false);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const saveSignature = () => {
    if (isEmpty) {
      toast.error("Please sign before saving");
      return;
    }
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
            <Pen className="w-5 h-5 text-[#23F7DD]" />
            Semnătură / Signature
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="border-2 border-dashed border-white/20 rounded-lg overflow-hidden bg-white">
            <canvas
              ref={canvasRef}
              width={450}
              height={150}
              className="w-full touch-none cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
          
          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={clearCanvas}
              className="border-white/20 text-[#94A3B8] hover:text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Șterge / Clear
            </Button>
            
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Anulează
              </Button>
              <Button
                type="button"
                onClick={saveSignature}
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
              >
                <Save className="w-4 h-4 mr-2" />
                Salvează
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function LocalProjectSJA() {
  const { projectId, sjaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [project, setProject] = useState(null);
  const [savedSjaId, setSavedSjaId] = useState(null);
  
  // Language state
  const [language, setLanguage] = useState("no"); // Default to Norwegian
  const t = TRANSLATIONS[language];
  
  // Import modal state
  const [availableSJAs, setAvailableSJAs] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);

  const [formData, setFormData] = useState({
    job_description: "",
    work_location: "",
    work_date: new Date().toISOString().split('T')[0],
    hazards: [],
    ppe_required: ["helmet", "safety_shoes", "hi_vis"],
    additional_measures: "",
    emergency_contact: "",
    nearest_hospital: "",
    first_aid_location: "",
    prepared_by: "",
    prepared_date: new Date().toISOString().split('T')[0],
    language: "no", // Default language
    participants: [], // Array of {company, name, signature}
  });

  // Signature modal state for participants
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureParticipantIndex, setSignatureParticipantIndex] = useState(null);

  const isEditing = sjaId && sjaId !== "new";

  useEffect(() => {
    fetchProject();
    if (isEditing) {
      fetchSJA();
    } else {
      fetchAvailableSJAs();
    }
  }, [projectId, sjaId]);

  useEffect(() => {
    if (user && !formData.prepared_by) {
      setFormData(prev => ({
        ...prev,
        prepared_by: user.name || ""
      }));
    }
  }, [user]);
  
  // Sync language state with formData
  useEffect(() => {
    setLanguage(formData.language || "no");
  }, [formData.language]);

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    setFormData(prev => ({ ...prev, language: newLang }));
  };

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}`);
      setProject(res.data);
      // Pre-fill work location from project
      if (!formData.work_location && res.data.location_address) {
        setFormData(prev => ({
          ...prev,
          work_location: `${res.data.location_address}, ${res.data.location_city || ''}`
        }));
      }
    } catch (error) {
      toast.error("Failed to load project");
    }
  };

  const fetchSJA = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}/sja/${sjaId}`);
      setFormData({
        job_description: res.data.job_description || "",
        work_location: res.data.work_location || "",
        work_date: res.data.work_date || "",
        hazards: res.data.hazards || [],
        ppe_required: res.data.ppe_required || ["helmet", "safety_shoes", "hi_vis"],
        additional_measures: res.data.additional_measures || "",
        emergency_contact: res.data.emergency_contact || "",
        nearest_hospital: res.data.nearest_hospital || "",
        first_aid_location: res.data.first_aid_location || "",
        prepared_by: res.data.prepared_by || "",
        prepared_date: res.data.prepared_date || "",
        language: res.data.language || "no",
        participants: res.data.participants || [],
      });
      setSavedSjaId(sjaId);
    } catch (error) {
      toast.error("Failed to load SJA");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSJAs = async () => {
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}/sja`);
      setAvailableSJAs(res.data || []);
    } catch (error) {
      console.error("Failed to fetch available SJAs:", error);
    }
  };

  const importFromSJA = async (sourceSjaId) => {
    setImporting(true);
    try {
      const res = await axios.get(`${API}/local-projects/${projectId}/sja/${sourceSjaId}`);
      const sourceSJA = res.data;
      
      setFormData({
        job_description: sourceSJA.job_description || "",
        work_location: sourceSJA.work_location || "",
        work_date: new Date().toISOString().split('T')[0], // Reset to today
        hazards: sourceSJA.hazards || [],
        ppe_required: sourceSJA.ppe_required || ["helmet", "safety_shoes", "hi_vis"],
        additional_measures: sourceSJA.additional_measures || "",
        emergency_contact: sourceSJA.emergency_contact || "",
        nearest_hospital: sourceSJA.nearest_hospital || "",
        first_aid_location: sourceSJA.first_aid_location || "",
        prepared_by: user?.name || "",
        prepared_date: new Date().toISOString().split('T')[0],
      });
      
      setShowImportModal(false);
      toast.success(`Importat SJA ${sourceSJA.sja_code} cu ${sourceSJA.hazards?.length || 0} hazarde`);
    } catch (error) {
      toast.error("Eroare la importarea SJA");
    } finally {
      setImporting(false);
    }
  };

  // Participant management
  const addParticipant = () => {
    const newParticipant = {
      participant_id: `part_${Date.now()}`,
      company: "",
      name: "",
      signature: null
    };
    setFormData({
      ...formData,
      participants: [...formData.participants, newParticipant]
    });
  };

  const updateParticipant = (index, field, value) => {
    const updated = [...formData.participants];
    updated[index][field] = value;
    setFormData({ ...formData, participants: updated });
  };

  const removeParticipant = (index) => {
    setFormData({
      ...formData,
      participants: formData.participants.filter((_, i) => i !== index)
    });
  };

  const openSignatureModal = (index) => {
    setSignatureParticipantIndex(index);
    setSignatureModalOpen(true);
  };

  const saveParticipantSignature = (signatureData) => {
    if (signatureParticipantIndex !== null) {
      updateParticipant(signatureParticipantIndex, 'signature', signatureData);
    }
    setSignatureModalOpen(false);
    setSignatureParticipantIndex(null);
    toast.success("Semnătura a fost salvată!");
  };

  // Hazard management
  const addHazard = (hazardTemplate) => {
    const newHazard = {
      hazard_id: `${hazardTemplate.id}_${Date.now()}`,
      description: t.hazardLabels[hazardTemplate.id] || hazardTemplate.id,
      probability: "medium",
      consequence: "medium",
      risk_level: "yellow",
      preventive_measures: "",
      responsible_person: "",
    };
    setFormData({
      ...formData,
      hazards: [...formData.hazards, newHazard],
    });
  };

  const updateHazard = (index, field, value) => {
    const updated = [...formData.hazards];
    updated[index][field] = value;
    
    if (field === "probability" || field === "consequence") {
      updated[index].risk_level = calculateRiskLevel(
        updated[index].probability,
        updated[index].consequence
      );
    }
    
    setFormData({ ...formData, hazards: updated });
  };

  const removeHazard = (index) => {
    setFormData({
      ...formData,
      hazards: formData.hazards.filter((_, i) => i !== index),
    });
  };

  const togglePPE = (ppeId) => {
    const current = formData.ppe_required;
    if (current.includes(ppeId)) {
      setFormData({
        ...formData,
        ppe_required: current.filter(id => id !== ppeId)
      });
    } else {
      setFormData({
        ...formData,
        ppe_required: [...current, ppeId]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.hazards.length === 0) {
      toast.error("Trebuie să adaugi cel puțin un hazard");
      return;
    }
    
    setSubmitting(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/local-projects/${projectId}/sja/${sjaId}`, formData);
        toast.success("SJA actualizat cu succes!");
      } else {
        const res = await axios.post(`${API}/local-projects/${projectId}/sja`, formData);
        setSavedSjaId(res.data.sja_id);
        navigate(`/local-projects/${projectId}/sja/${res.data.sja_id}`, { replace: true });
        toast.success("SJA creat cu succes!");
      }
    } catch (error) {
      toast.error("Eroare la salvarea SJA");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    if (!savedSjaId) return;
    
    setGeneratingPdf(true);
    try {
      // Open PDF directly in new tab
      const pdfUrl = `${API}/local-projects/${projectId}/sja/${savedSjaId}/pdf`;
      window.open(pdfUrl, '_blank');
      
      toast.success("PDF deschis în tab nou! Folosește Print (Ctrl+P) pentru a salva ca PDF.");
    } catch (error) {
      toast.error("Eroare la generarea PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={adminNavItems} title="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={adminNavItems} title={isEditing ? "Edit SJA" : "New SJA"}>
      <form onSubmit={handleSubmit} className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/local-projects/${projectId}`)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Unbounded'] flex items-center gap-2">
                <Shield className="w-6 h-6 text-red-400" />
                {isEditing ? "Edit SJA" : "New SJA"}
              </h1>
              {project && (
                <p className="text-[#94A3B8]">{project.title}</p>
              )}
            </div>
          </div>
          
          <div className="flex gap-3 items-center">
            {/* Language Selector */}
            <div className="flex items-center gap-2 bg-black/30 rounded-lg p-1 border border-white/10">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    language === lang.code
                      ? "bg-[#23F7DD] text-[#050505]"
                      : "text-[#94A3B8] hover:text-white hover:bg-white/10"
                  }`}
                  title={lang.label}
                >
                  <span>{lang.flag}</span>
                  <span className="hidden sm:inline">{lang.code.toUpperCase()}</span>
                </button>
              ))}
            </div>
            
            {!isEditing && availableSJAs.length > 0 && (
              <Button
                type="button"
                onClick={() => setShowImportModal(true)}
                variant="outline"
                className="border-[#FF5F1F]/50 text-[#FF5F1F] hover:bg-[#FF5F1F]/10"
              >
                <Copy className="w-4 h-4 mr-2" />
                {t.importPrevious}
              </Button>
            )}
            {savedSjaId && (
              <Button
                type="button"
                onClick={downloadPdf}
                disabled={generatingPdf}
                variant="outline"
                className="border-[#23F7DD]/50 text-[#23F7DD] hover:bg-[#23F7DD]/10"
              >
                {generatingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileDown className="w-4 h-4 mr-2" />
                )}
                {t.downloadPdf}
              </Button>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isEditing ? t.update : t.save}
            </Button>
          </div>
        </div>

        {/* Job Info Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded']">{t.generalInfo}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">{t.jobDescription} *</Label>
              <Textarea
                value={formData.job_description}
                onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                placeholder="..."
                rows={3}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {t.workLocation}
                </Label>
                <Input
                  value={formData.work_location}
                  onChange={(e) => setFormData({ ...formData, work_location: e.target.value })}
                  placeholder="..."
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t.workDate}
                </Label>
                <Input
                  type="date"
                  value={formData.work_date}
                  onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hazards Section */}
        <Card className="bg-[#0A0A0A] border-red-500/30">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              {t.identifiedHazards} *
              <Badge variant="outline" className="ml-2 border-white/20">
                {formData.hazards.length} {t.hazards}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick add hazards */}
            <div className="flex flex-wrap gap-2">
              {COMMON_HAZARDS.map((hazard) => (
                <Button
                  key={hazard.id}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addHazard(hazard)}
                  className="border-white/20 text-[#94A3B8] hover:border-red-500/50 hover:text-red-400"
                  title={hazard.description}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {t.hazardLabels[hazard.id] || hazard.id}
                </Button>
              ))}
            </div>

            {/* Hazard list */}
            {formData.hazards.length > 0 ? (
              <div className="space-y-3">
                {formData.hazards.map((hazard, index) => (
                  <div key={hazard.hazard_id || index}>
                    <div className="p-4 bg-black/30 rounded-lg border border-white/10">
                      <div className="flex items-start justify-between mb-3">
                        <Input
                          value={hazard.description}
                          onChange={(e) => updateHazard(index, "description", e.target.value)}
                          className="bg-transparent border-none text-white font-medium p-0 h-auto focus:ring-0 text-lg"
                          placeholder="Descrierea hazardului"
                        />
                        <div className="flex items-center gap-2">
                          {getRiskBadge(hazard.risk_level, t)}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeHazard(index)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-1 h-auto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                        <div>
                          <Label className="text-xs text-[#64748B]">{t.probability}</Label>
                          <select
                            value={hazard.probability}
                            onChange={(e) => updateHazard(index, "probability", e.target.value)}
                            className="w-full bg-black/50 border border-white/10 text-white text-sm px-2 py-1.5 rounded"
                          >
                            <option value="low">{t.low}</option>
                            <option value="medium">{t.medium}</option>
                            <option value="high">{t.high}</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs text-[#64748B]">{t.consequence}</Label>
                          <select
                            value={hazard.consequence}
                            onChange={(e) => updateHazard(index, "consequence", e.target.value)}
                            className="w-full bg-black/50 border border-white/10 text-white text-sm px-2 py-1.5 rounded"
                          >
                            <option value="low">{t.minor}</option>
                            <option value="medium">{t.moderate}</option>
                            <option value="high">{t.severe}</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs text-[#64748B]">{t.responsiblePerson}</Label>
                          <Input
                            value={hazard.responsible_person || ""}
                            onChange={(e) => updateHazard(index, "responsible_person", e.target.value)}
                            placeholder="..."
                            className="bg-black/50 border-white/10 text-white text-sm h-8"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-[#64748B]">{t.preventiveMeasures} *</Label>
                        <Textarea
                          value={hazard.preventive_measures || ""}
                          onChange={(e) => updateHazard(index, "preventive_measures", e.target.value)}
                          placeholder="..."
                          rows={2}
                          className="bg-black/50 border-white/10 text-white text-sm"
                        />
                      </div>
                    </div>
                    {/* Visual separator between hazards */}
                    {index < formData.hazards.length - 1 && (
                      <div className="flex items-center gap-3 my-4">
                        <div className="flex-1 h-[2px] bg-gradient-to-r from-red-500/50 via-yellow-500/50 to-green-500/50 rounded-full" />
                        <span className="text-xs text-[#64748B] font-medium">#{index + 2}</span>
                        <div className="flex-1 h-[2px] bg-gradient-to-r from-green-500/50 via-yellow-500/50 to-red-500/50 rounded-full" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#94A3B8] border-2 border-dashed border-red-500/30 rounded-lg">
                <AlertTriangle className="w-10 h-10 mx-auto mb-2 text-red-400/50" />
                <p className="text-white">{language === 'ro' ? 'Click pe butoanele de mai sus pentru a adăuga hazarde' : language === 'no' ? 'Klikk på knappene ovenfor for å legge til farer' : 'Click the buttons above to add hazards'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PPE Section */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <HardHat className="w-5 h-5 text-yellow-400" />
              {t.ppe}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PPE_OPTIONS.map((ppe) => (
                <div
                  key={ppe.id}
                  onClick={() => togglePPE(ppe.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center gap-2 ${
                    formData.ppe_required.includes(ppe.id)
                      ? "bg-green-500/20 border-green-500/50 text-green-400"
                      : "bg-black/30 border-white/10 text-[#94A3B8] hover:border-white/30"
                  }`}
                >
                  <span className="text-xl">{ppe.icon}</span>
                  <span className="text-sm">{t.ppeLabels[ppe.id] || ppe.id}</span>
                  {formData.ppe_required.includes(ppe.id) && (
                    <CheckCircle2 className="w-4 h-4 ml-auto" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Emergency Info */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-400" />
              {t.emergencyInfo}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">{t.emergencyContact}</Label>
                <Input
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  placeholder="112"
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">{t.nearestHospital}</Label>
                <Input
                  value={formData.nearest_hospital}
                  onChange={(e) => setFormData({ ...formData, nearest_hospital: e.target.value })}
                  placeholder="..."
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">{t.firstAidLocation}</Label>
                <Input
                  value={formData.first_aid_location}
                  onChange={(e) => setFormData({ ...formData, first_aid_location: e.target.value })}
                  placeholder="..."
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">{t.additionalMeasures}</Label>
              <Textarea
                value={formData.additional_measures}
                onChange={(e) => setFormData({ ...formData, additional_measures: e.target.value })}
                placeholder="..."
                rows={2}
                className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
              />
            </div>
          </CardContent>
        </Card>

        {/* Participants Section */}
        <Card className="bg-[#0A0A0A] border-[#23F7DD]/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white font-['Unbounded'] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#23F7DD]" />
                {t.participants}
                <Badge variant="outline" className="ml-2 border-white/20">
                  {formData.participants.length}
                </Badge>
              </CardTitle>
              <Button
                type="button"
                onClick={addParticipant}
                size="sm"
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t.addParticipant}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.participants.length > 0 ? (
              <div className="space-y-3">
                {formData.participants.map((participant, index) => (
                  <div 
                    key={participant.participant_id || index} 
                    className="p-4 bg-black/30 rounded-lg border border-white/10 hover:border-[#23F7DD]/30 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {/* Company */}
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs text-[#64748B] flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {t.company}
                        </Label>
                        <Input
                          value={participant.company || ""}
                          onChange={(e) => updateParticipant(index, "company", e.target.value)}
                          placeholder="AllStillas AS"
                          className="bg-black/50 border-white/10 text-white text-sm h-9"
                        />
                      </div>
                      
                      {/* Name */}
                      <div className="flex-1 space-y-2">
                        <Label className="text-xs text-[#64748B] flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {t.fullName}
                        </Label>
                        <Input
                          value={participant.name || ""}
                          onChange={(e) => updateParticipant(index, "name", e.target.value)}
                          placeholder="Ion Popescu"
                          className="bg-black/50 border-white/10 text-white text-sm h-9"
                        />
                      </div>
                      
                      {/* Signature */}
                      <div className="space-y-2">
                        <Label className="text-xs text-[#64748B] flex items-center gap-1">
                          <Pen className="w-3 h-3" />
                          {t.signature}
                        </Label>
                        {participant.signature ? (
                          <div className="relative group">
                            <div className="bg-white rounded p-1 h-9 w-24 flex items-center justify-center border border-[#23F7DD]/50">
                              <img 
                                src={participant.signature} 
                                alt="Signature"
                                className="max-h-7 object-contain"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => updateParticipant(index, "signature", null)}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openSignatureModal(index)}
                            className="h-9 w-24 border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10 text-xs"
                          >
                            <Pen className="w-3 h-3 mr-1" />
                            {t.addSignature}
                          </Button>
                        )}
                      </div>
                      
                      {/* Delete button */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeParticipant(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-9 px-2 mt-6"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#94A3B8] border-2 border-dashed border-[#23F7DD]/20 rounded-lg">
                <Users className="w-10 h-10 mx-auto mb-2 text-[#23F7DD]/50" />
                <p className="text-white">{t.noParticipants}</p>
                <p className="text-sm text-[#64748B] mt-1">
                  {language === 'ro' ? 'Click butonul de mai sus pentru a adăuga participanți' : 
                   language === 'no' ? 'Klikk knappen ovenfor for å legge til deltakere' : 
                   'Click the button above to add participants'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Prepared By */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {t.preparedBy}
                </Label>
                <Input
                  value={formData.prepared_by}
                  onChange={(e) => setFormData({ ...formData, prepared_by: e.target.value })}
                  placeholder="..."
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8] flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {t.preparedDate}
                </Label>
                <Input
                  type="date"
                  value={formData.prepared_date}
                  onChange={(e) => setFormData({ ...formData, prepared_date: e.target.value })}
                  className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bottom Submit */}
        <div className="flex justify-end gap-4 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/local-projects/${projectId}`)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            {t.cancel}
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold min-w-[150px]"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isEditing ? t.update : t.save}
          </Button>
        </div>
      </form>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#FF5F1F]" />
              {t.importPrevious}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-[#94A3B8]">
              Selectează un SJA din care să importezi hazardele și setările.
            </p>
            
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {availableSJAs.map((sja) => (
                <div
                  key={sja.sja_id}
                  className="p-4 rounded-lg border border-white/10 hover:border-[#FF5F1F]/50 cursor-pointer transition-colors bg-black/30 hover:bg-black/50"
                  onClick={() => !importing && importFromSJA(sja.sja_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-red-400" />
                      <div>
                        <p className="font-medium text-white">{sja.sja_code}</p>
                        <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                          <Calendar className="w-3 h-3" />
                          {sja.work_date || sja.prepared_date}
                          <span>•</span>
                          <span>{sja.hazards?.length || 0} hazarde</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowImportModal(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Anulează
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Participant Signature Modal */}
      <ParticipantSignaturePad
        isOpen={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setSignatureParticipantIndex(null);
        }}
        onSave={saveParticipantSignature}
      />
    </DashboardLayout>
  );
}
